import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import type { ApiEnvelope, PageEnvelope } from "@/lib/types";
import type {
  BrandAssignment,
  ConsumerPlane,
  EffectivePresentation,
  PresentationTemplate,
  TemplateBlock,
  TemplateKind,
  VisualTheme,
} from "./types";

const PRESENTATION_KEY = "governed-presentation";

export function usePresentationTemplates() {
  return useQuery({
    queryKey: [PRESENTATION_KEY, "templates"],
    queryFn: async () =>
      (
        await api.get<PageEnvelope<PresentationTemplate>>(
          "/presentation/templates/",
        )
      ).data,
  });
}

export function useVisualThemes() {
  return useQuery({
    queryKey: [PRESENTATION_KEY, "themes"],
    queryFn: async () =>
      (await api.get<PageEnvelope<VisualTheme>>("/presentation/themes/")).data,
  });
}

export function useBrandAssignments() {
  return useQuery({
    queryKey: [PRESENTATION_KEY, "assignments"],
    queryFn: async () =>
      (
        await api.get<PageEnvelope<BrandAssignment>>(
          "/presentation/assignments/",
        )
      ).data,
  });
}

export function useEffectivePresentation(
  plane: ConsumerPlane,
  enabled: boolean,
  templateKind: TemplateKind = "proposal",
) {
  return useQuery({
    queryKey: [PRESENTATION_KEY, "effective", plane, templateKind],
    queryFn: async () =>
      (
        await api.get<ApiEnvelope<EffectivePresentation>>(
          "/presentation/effective/",
          {
            params: { plane, template_kind: templateKind },
          },
        )
      ).data.data,
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

function invalidator(qc: ReturnType<typeof useQueryClient>) {
  return () => qc.invalidateQueries({ queryKey: [PRESENTATION_KEY] });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      family_key: string;
      name: string;
      template_kind: TemplateKind;
      consumer_planes: ConsumerPlane[];
      blocks: TemplateBlock[];
    }) =>
      (
        await api.post<ApiEnvelope<PresentationTemplate>>(
          "/presentation/templates/",
          payload,
        )
      ).data.data,
    onSuccess: invalidator(qc),
  });
}

export function useCreateTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      family_key: string;
      name: string;
      consumer_planes: ConsumerPlane[];
      tokens: Record<string, string | number>;
    }) =>
      (
        await api.post<ApiEnvelope<VisualTheme>>(
          "/presentation/themes/",
          payload,
        )
      ).data.data,
    onSuccess: invalidator(qc),
  });
}

export function useCreateBrandAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      organization: string;
      workspace?: string;
      consumer_plane: ConsumerPlane;
      template_kind: TemplateKind;
      theme_version?: string;
      template_version?: string;
      allow_workspace_overrides: boolean;
      replace_existing: boolean;
      replacement_reason?: string;
    }) =>
      (
        await api.post<ApiEnvelope<BrandAssignment>>(
          "/presentation/assignments/",
          payload,
        )
      ).data.data,
    onSuccess: invalidator(qc),
  });
}

export function useRevokeBrandAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<ApiEnvelope<BrandAssignment>>(
          `/presentation/assignments/${id}/revoke/`,
          { reason: "Governed operator revocation." },
        )
      ).data.data,
    onSuccess: invalidator(qc),
  });
}

export function useVersionAction(resource: "templates" | "themes") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      reason,
    }: {
      id: string;
      action: "publish" | "revoke" | "supersede" | "rollback";
      reason?: string;
    }) =>
      (
        await api.post<ApiEnvelope<PresentationTemplate | VisualTheme>>(
          `/presentation/${resource}/${id}/${action}/`,
          action === "publish" || action === "revoke"
            ? { reason: reason ?? "Governed operator action." }
            : {},
        )
      ).data.data,
    onSuccess: invalidator(qc),
  });
}
